/**
 * Favourites — server is source of truth (cross-device sync).
 *
 * The local Dexie table is a write-through cache so the Favourites page
 * renders instantly on cold start; we reconcile against the server on
 * every load. Optimistic updates: insert/delete locally, then POST/DELETE.
 * On failure we revert and surface the error.
 *
 * Catchup-stream-id derivation (per spec, Option A): when a live stream
 * has tv_archive=1 we scan the in-memory catalog for the catchup
 * counterpart with the same epg_channel_id and send its id alongside.
 *
 * "Smart" combined live+catchup row: catchup-only Stream rows
 * (kind === "catchup") MUST NOT be favourited as their own entry —
 * Browse code paths only fav live channels. UI hides the heart on
 * catchup rows.
 */
import { api } from "./api";
import { db } from "./db";
export { deriveCatchupStreamId } from "./catchupLink";

export type FavStreamKind = "live" | "vod" | "series";

export interface ServerFavorite {
  id: number;
  stream_kind: FavStreamKind;
  stream_id: string;
  title: string;
  poster: string | null;
  has_catchup: boolean;
  catchup_stream_id: string | null;
  sort_index: number;
  created_at: string;
  updated_at: string;
}

interface ListResp { items: ServerFavorite[] }

export const Favorites = {
  list: (pid: number) =>
    api.get<ListResp>(`/api/playlists/${pid}/favorites`).then((r) => r.data.items),

  upsert: (pid: number, body: {
    stream_kind: FavStreamKind;
    stream_id: string;
    title: string;
    poster?: string | null;
    has_catchup?: boolean;
    catchup_stream_id?: string | null;
  }) =>
    api.post<ServerFavorite>(`/api/playlists/${pid}/favorites`, body).then((r) => r.data),

  remove: (pid: number, kind: FavStreamKind, streamId: string) =>
    api.delete<void>(
      `/api/playlists/${pid}/favorites/${kind}/${encodeURIComponent(streamId)}`
    ),

  reorder: (pid: number, ids: number[]) =>
    api.patch<{ updated: number }>(`/api/playlists/${pid}/favorites/order`, { ids })
      .then((r) => r.data),
};


/** Replace the local Dexie cache with the server's view for one playlist.
 * Called on login + on activePlaylist change + after every mutation. */
export async function syncFavoritesCache(
  userId: number,
  playlistId: number,
  serverItems: ServerFavorite[],
): Promise<void> {
  await db.favorites.where("playlistId").equals(playlistId).delete();
  const rows = serverItems.map((s) => ({
    key: `${userId}:${playlistId}:${s.stream_kind}:${s.stream_id}`,
    serverId: s.id,
    userId,
    playlistId,
    streamKind: s.stream_kind,
    streamId: s.stream_id,
    title: s.title,
    poster: s.poster,
    hasCatchup: s.has_catchup,
    catchupStreamId: s.catchup_stream_id,
    sortIndex: s.sort_index,
    addedAt: Date.parse(s.created_at) || Date.now(),
  }));
  if (rows.length) await db.favorites.bulkPut(rows);
}
